import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { FLEXXI_PLAN_STATUS_LABEL, type FlexxiPlanStatus } from "@gaole/shared";
import { api } from "../lib/api";
import { ListPageShell } from "../components/shell/ListPageShell";
import { DataTable, type Column } from "../components/DataTable";
import { Badge, statusColor } from "../components/ui";
import { rm, fmtDateDMY, fmtDateTimeDMY } from "../lib/format";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

interface PlanRow {
  id: string;
  userEmail: string;
  userName: string;
  status: FlexxiPlanStatus;
  statusLabel: string;
  productName: string;
  channel: string;
  paidTotal: number;
  totalPayable: number;
  templateCode: string;
  updatedAt: string;
}

type PlanDetail = {
  plan: {
    id: string;
    userEmail: string;
    userName: string | null;
    status: FlexxiPlanStatus;
    statusLabel: string;
    channel: string;
    productName: string;
    templateCode: string;
    templateName: string;
    termMonths: number;
    paidTotal: number;
    totalPayable: number;
    remaining: number;
    depositAmount: number;
    lateFeesAccrued: number;
    completedAt: string | null;
  };
  nextMilestone: {
    sequence: number;
    dueDate: string;
    amountDue: number;
    amountPaid: number;
    amountRemaining: number;
    status: string;
  } | null;
  installments: {
    id: string;
    sequence: number;
    dueDate: string;
    amountDue: number;
    amountPaid: number;
    status: string;
    lateFeeAccrued: number;
  }[];
  payments: {
    id: string;
    kind: string;
    amount: number;
    createdAt: string;
    note: string | null;
  }[];
  suggestedPayAmount: number;
};

const PAYMENT_KIND_LABEL: Record<string, string> = {
  DEPOSIT: "Deposit",
  INSTALLMENT: "Instalment",
  LATE_FEE: "Late fee",
  ADJUSTMENT: "Adjustment",
};

function installmentStatusLabel(status: string): string {
  return status.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function FlexxiPlans() {
  const [status, setStatus] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["flexxi", "plans", status],
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      return api<{ plans: PlanRow[]; total: number }>(`/admin/flexxi/plans?${params}`);
    },
  });

  const detailQ = useQuery({
    queryKey: ["flexxi", "plan", selectedId],
    queryFn: () => api<PlanDetail>(`/admin/flexxi/plans/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const detail = detailQ.data;
  const selectedRow = q.data?.plans.find((r) => r.id === selectedId) ?? null;

  const columns: Column<PlanRow>[] = [
    { key: "user", header: "Customer", render: (r) => r.userName || r.userEmail },
    {
      key: "product",
      header: "Item",
      render: (r) => (
        <span>
          {r.productName}
          {r.channel === "MARKETPLACE" ? (
            <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted">Marketplace</span>
          ) : null}
        </span>
      ),
    },
    { key: "plan", header: "Plan", render: (r) => r.templateCode },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge color={r.status === "OVERDUE" || r.status === "DEFAULTED" ? "red" : "gray"}>
          {FLEXXI_PLAN_STATUS_LABEL[r.status].staff}
        </Badge>
      ),
    },
    {
      key: "paid",
      header: "Paid",
      render: (r) => `${rm(r.paidTotal)} / ${rm(r.totalPayable)}`,
      className: "text-right",
    },
    { key: "updated", header: "Updated", render: (r) => fmtDateTimeDMY(r.updatedAt) },
  ];

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={q.data?.plans ?? []}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        entityLabel="Plan"
        exportFilename="flexxi-plans"
        selectable={false}
        rowMenuFirst
        onRowClick={(r) => setSelectedId(r.id)}
        activeKey={selectedId ?? undefined}
        rowActions={{
          extra: (r) => [{ label: "View plan", onClick: () => setSelectedId(r.id) }],
        }}
        toolbar={{
          views: {
            value: status,
            onChange: setStatus,
            options: [
              { value: "", label: "All" },
              ...(["ACTIVE", "OVERDUE", "DEFAULTED", "COMPLETED"] as FlexxiPlanStatus[]).map((s) => ({
                value: s,
                label: FLEXXI_PLAN_STATUS_LABEL[s].staff,
              })),
            ],
          },
          onRefresh: () => void q.refetch(),
        }}
        empty={{ icon: CalendarClock, title: "No FlexxiPlan contracts", hint: "Plans appear after customers use instalment checkout." }}
      />

      <RightSidebarPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={selectedRow?.productName ?? "FlexxiPlan"}
        subtitle={selectedRow ? `${selectedRow.userName || selectedRow.userEmail} · ${selectedRow.templateCode}` : undefined}
      >
        {detailQ.isLoading ? (
          <p className="text-sm text-muted">Loading schedule…</p>
        ) : detail ? (
          <div className="space-y-5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={statusColor(detail.plan.status === "COMPLETED" ? "COMPLETED" : "PENDING")}>
                {detail.plan.statusLabel}
              </Badge>
              {detail.plan.channel === "MARKETPLACE" ? (
                <span className="text-[10px] uppercase tracking-wide text-muted">Marketplace</span>
              ) : null}
            </div>

            <div className="rounded-xl border border-line bg-surface-2/50 p-3 text-xs">
              <p className="text-muted">Paid to date</p>
              <p className="mt-1 font-display text-lg text-foreground">
                {rm(detail.plan.paidTotal)}{" "}
                <span className="text-sm font-normal text-muted">/ {rm(detail.plan.totalPayable)}</span>
              </p>
              {detail.plan.remaining > 0 ? (
                <p className="mt-1 text-muted">{rm(detail.plan.remaining)} remaining</p>
              ) : null}
              <p className="mt-2 text-muted">
                {detail.plan.depositAmount > 0
                  ? `Deposit at checkout: ${rm(detail.plan.depositAmount)}`
                  : "No deposit — first instalment paid at checkout"}
              </p>
            </div>

            {detail.nextMilestone ? (
              <div className="rounded-xl border border-brand-pink/30 bg-brand-pink/5 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-pink">Next milestone</p>
                <p className="mt-2 font-medium text-foreground">
                  Instalment #{detail.nextMilestone.sequence} · due {fmtDateDMY(detail.nextMilestone.dueDate)}
                </p>
                <p className="mt-1 text-xs text-muted">
                  {rm(detail.nextMilestone.amountRemaining)} due
                  {detail.nextMilestone.amountPaid > 0
                    ? ` (${rm(detail.nextMilestone.amountPaid)} paid of ${rm(detail.nextMilestone.amountDue)})`
                    : ` of ${rm(detail.nextMilestone.amountDue)}`}
                </p>
                <p className="mt-1 text-[11px] capitalize text-muted">
                  Status: {installmentStatusLabel(detail.nextMilestone.status)}
                </p>
                <p className="mt-2 text-[11px] text-muted">
                  Min. suggested payment now: {rm(detail.suggestedPayAmount)}
                </p>
              </div>
            ) : detail.plan.completedAt ? (
              <p className="text-xs text-muted">Plan completed {fmtDateDMY(detail.plan.completedAt)}.</p>
            ) : (
              <p className="text-xs text-muted">No upcoming instalments.</p>
            )}

            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted">Payment schedule</p>
              <ul className="divide-y divide-line rounded-xl border border-line">
                {detail.installments.map((inst) => {
                  const isNext = detail.nextMilestone?.sequence === inst.sequence;
                  return (
                    <li
                      key={inst.id}
                      className={`flex items-start justify-between gap-3 px-3 py-2.5 text-xs ${isNext ? "bg-brand-pink/[0.06]" : ""}`}
                    >
                      <div>
                        <p className="font-medium text-foreground">#{inst.sequence}</p>
                        <p className="text-muted">{fmtDateDMY(inst.dueDate)}</p>
                      </div>
                      <div className="text-right">
                        <p className="tabular-nums text-foreground">
                          {rm(inst.amountPaid)} / {rm(inst.amountDue)}
                        </p>
                        <p className="capitalize text-muted">{installmentStatusLabel(inst.status)}</p>
                        {inst.lateFeeAccrued > 0 ? (
                          <p className="text-amber-400/90">Late fee {rm(inst.lateFeeAccrued)}</p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted">Payments made</p>
              {detail.payments.length === 0 ? (
                <p className="text-xs text-muted">No payment records yet.</p>
              ) : (
                <ul className="divide-y divide-line rounded-xl border border-line">
                  {detail.payments.map((p) => (
                    <li key={p.id} className="flex items-start justify-between gap-3 px-3 py-2.5 text-xs">
                      <div>
                        <p className="font-medium text-foreground">{PAYMENT_KIND_LABEL[p.kind] ?? p.kind}</p>
                        <p className="text-muted">{fmtDateTimeDMY(p.createdAt)}</p>
                        {p.note ? <p className="mt-0.5 text-muted-2">{p.note}</p> : null}
                      </div>
                      <p className="shrink-0 tabular-nums font-medium text-foreground">{rm(p.amount)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted">Could not load plan detail.</p>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
